import { useEffect, useState } from "react"
import { useApp } from "@/lib/app-context"
import { useLanguage } from "@/lib/language-context"
import { useToast } from "@/hooks/use-toast"
import { Header } from "@/components/header"
import { HomeScreen } from "@/components/home-screen"
import { PassportUpload } from "@/components/passport-upload"
import { PaymentScreen } from "@/components/payment-screen"
import { SuccessScreen } from "@/components/success-screen"
import { StatusScreen } from "@/components/status-screen"
import { FAQScreen } from "@/components/faq-screen"
import { BottomNav } from "@/components/bottom-nav"
import { LanguageSelector } from "@/components/language-selector"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { CheckCircle2 } from "lucide-react"

const STRIPE_RETURN_PARAM = "redirect_status"

export default function App() {
  const { currentScreen, setCurrentScreen, setApplicationId } = useApp()
  const { t } = useLanguage()
  const { toast } = useToast()
  const [showStripeReturnSuccessPopup, setShowStripeReturnSuccessPopup] = useState(false)
  const [pendingStripeReturnAppId, setPendingStripeReturnAppId] = useState<string | null>(null)

  // Handle return from Stripe redirect (e.g. 3D Secure) — show success popup first, then go to success screen on dismiss
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get(STRIPE_RETURN_PARAM) === "succeeded") {
      const storedId = sessionStorage.getItem("vimana_payment_application_id")
      if (storedId) {
        setApplicationId(storedId)
        setPendingStripeReturnAppId(storedId)
        setShowStripeReturnSuccessPopup(true)
        try {
          sessionStorage.removeItem("vimana_payment_application_id")
        } catch (_) {}
      }
      window.history.replaceState({}, "", window.location.pathname || "/")
    }
  }, [setApplicationId])

  const handleStripeReturnSuccessPopupClose = () => {
    setShowStripeReturnSuccessPopup(false)
    if (pendingStripeReturnAppId) {
      toast({
        title: t.paymentSuccessToast,
        variant: "default",
      })
      setCurrentScreen("apply-success")
      setPendingStripeReturnAppId(null)
    }
  }

  const renderScreen = () => {
    switch (currentScreen) {
      case "home":
        return <HomeScreen />
      case "apply-country":
      case "apply-passport":
        return <PassportUpload />
      case "apply-payment":
        return <PaymentScreen />
      case "apply-success":
        return <SuccessScreen />
      case "status":
        return <StatusScreen />
      case "faq":
        return <FAQScreen />
      default:
        return <HomeScreen />
    }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <LanguageSelector />
      <Header />
      <main className="flex-1 pb-16">{renderScreen()}</main>
      <BottomNav />

      {/* Payment succeeded popup when returning from Stripe redirect (e.g. 3D Secure) */}
      <Dialog
        open={showStripeReturnSuccessPopup}
        onOpenChange={(open) => !open && handleStripeReturnSuccessPopupClose()}
      >
        <DialogContent showCloseButton={false} className="max-w-sm text-center">
          <DialogHeader>
            <div className="flex justify-center mb-2">
              <CheckCircle2 className="h-14 w-14 text-green-600" aria-hidden />
            </div>
            <DialogTitle className="text-xl">{t.paymentSuccessPopupTitle}</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">
            {t.paymentSuccessPopupDesc}
          </p>
          <DialogFooter className="flex justify-center sm:justify-center pt-2">
            <Button onClick={handleStripeReturnSuccessPopupClose}>{t.ok}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
